"use client";

import { c as _c } from "react/compiler-runtime";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useMemo, useRef, useState, useTransition } from "react";
import { cn } from "@local/utils/src/cn";
import { Flag } from "./flags";
import { marketsData } from "@local/offer/src/lib/const/marketsData";
import { useOutsideClickCapture } from "@local/utils/src/useOutsideClickCapture";
import { CheckIcon } from "@local/ui/src/components/icons/Check";
import Cookies from "js-cookie";
import { I18n } from "@local/offer/src/lib/i18n";
import { useRouter } from "next/navigation";
import type { Route } from "next";
import { trackAction } from "@local/tracking/src/trackActions";
import { ArrowDownIcon } from "@local/ui/src/components/ui/icons";
import { LoadingSpinner } from "@local/ui/src/LoadingSpinner";
import { getT } from "@local/i18n/src/t";
import { composeMarketSlug, getLanguageForMarket, languagesForMarket, marketBaseOf, parseMarketSlug } from "@local/i18n/src/languages";
import { nativeLanguageName } from "@local/i18n/src/languageNames";
import { setLangPref } from "@local/i18n/src/langCookie";
import { a11y } from "@local/i18n/src/catalogs/a11y";
import { marketSelector } from "@local/i18n/src/catalogs/marketSelector";
import { countryNames, localizedCountryName } from "@local/i18n/src/catalogs/countryNames";
interface Props {
  selected: string;
  lightBackground?: boolean;
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
}
const MARKET_OPTIONS = marketsData.map(c => ({
  value: c.market,
  label: c.name,
  currencySymbol: c.currencySymbol
}));
const FlagWrapper = t0 => {
  const $ = _c(2);
  const {
    country
  } = t0;
  let t1;
  if ($[0] !== country) {
    t1 = <div className="flex aspect-square items-center justify-center rounded-full bg-gray-200 p-1.5"><div className="flex h-[18px] w-[18px] items-center justify-center overflow-hidden rounded-full"><Flag country={country} className="h-full w-full object-cover" width={16} height={16} /></div></div>;
    $[0] = country;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const LanguageMarketSelector = t0 => {
  const $ = _c(50);
  const {
    selected,
    lightBackground: t1,
    className,
    wrapperClassName: t2,
    disabled: t3
  } = t0;
  const lightBackground = t1 === undefined ? false : t1;
  const wrapperClassName = t2 === undefined ? "" : t2;
  const disabled = t3 === undefined ? false : t3;
  const {
    market: baseMarket,
    language: currentLang
  } = parseMarketSlug(selected.toLowerCase());
  const langs = languagesForMarket(baseMarket);
  const isMulti = langs.length > 1;
  const [regionOpen, setRegionOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const uniqueId = useId();
  const wrapperRef = useRef(null);
  const regionBtnRef = useRef(null);
  const langBtnRef = useRef(null);
  const regionListRef = useRef(null);
  const langListRef = useRef(null);
  const {
    push
  } = useRouter();
  const enabledOptions = _temp;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (e, container_0) => {
      const options = enabledOptions(container_0);
      if (options.length === 0) {
        return;
      }
      const currentIndex = options.indexOf(document.activeElement as HTMLButtonElement);
      const last = options.length - 1;
      let nextIndex;
      bb23: switch (e.key) {
        case "ArrowDown":
          {
            nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
            break bb23;
          }
        case "ArrowUp":
          {
            nextIndex = currentIndex <= 0 ? last : currentIndex - 1;
            break bb23;
          }
        case "Home":
          {
            nextIndex = 0;
            break bb23;
          }
        case "End":
          {
            nextIndex = last;
            break bb23;
          }
        default:
          {
            return;
          }
      }
      e.preventDefault();
      options[nextIndex]?.focus();
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const handleListboxKeyDown = t4;
  let t5;
  let t6;
  if ($[1] !== regionOpen) {
    t5 = () => {
      if (!regionOpen) {
        return;
      }
      enabledOptions(regionListRef.current)[0]?.focus();
    };
    t6 = [regionOpen];
    $[1] = regionOpen;
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[4] !== langOpen) {
    t7 = () => {
      if (!langOpen) {
        return;
      }
      enabledOptions(langListRef.current)[0]?.focus();
    };
    t8 = [langOpen];
    $[4] = langOpen;
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  useEffect(t7, t8);
  const t = getT(getLanguageForMarket(selected), {
    a11y,
    countryNames,
    marketSelector
  });
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setRegionOpen(false);
      setLangOpen(false);
    };
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  useOutsideClickCapture(wrapperRef, t9);
  let t10;
  let t11;
  if ($[8] !== langOpen || $[9] !== regionOpen) {
    t10 = () => {
      if (!regionOpen && !langOpen) {
        return;
      }
      const onEsc = e_0 => {
        if (e_0.key !== "Escape") {
          return;
        }
        if (langOpen) {
          setLangOpen(false);
          langBtnRef.current?.focus();
        }
        if (regionOpen) {
          setRegionOpen(false);
          regionBtnRef.current?.focus();
        }
      };
      document.addEventListener("keydown", onEsc);
      return () => document.removeEventListener("keydown", onEsc);
    };
    t11 = [regionOpen, langOpen];
    $[8] = langOpen;
    $[9] = regionOpen;
    $[10] = t10;
    $[11] = t11;
  } else {
    t10 = $[10];
    t11 = $[11];
  }
  useEffect(t10, t11);
  let t12;
  if ($[12] !== selected) {
    t12 = MARKET_OPTIONS.find(m => m.value === marketBaseOf(selected.toLowerCase()));
    $[12] = selected;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const selectedCountry = t12;
  const regionLabel = selectedCountry ? `${localizedCountryName(t, selectedCountry.value, selectedCountry.label)} (${selectedCountry.currencySymbol})` : "";
  let t13;
  if ($[14] !== push || $[15] !== selected) {
    t13 = targetSlug => {
      const url = I18n.buildMarketUrl(window.location.href, selected.toLowerCase(), targetSlug.toLowerCase());
      startTransition(() => push(url.href as Route));
    };
    $[14] = push;
    $[15] = selected;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const navigateTo = t13;
  let t14;
  if ($[17] !== navigateTo || $[18] !== selected) {
    t14 = marketValue => {
      const country = marketsData.find(c => c.market === marketBaseOf(marketValue.toLowerCase()));
      if (!country) {
        console.error(`User tried to change to an invalid country: ${marketValue}`);
        return;
      }
      Cookies.set("override_store", marketValue.toLowerCase());
      trackAction("market_selector_change", {
        from: selected.toLowerCase(),
        to: marketValue.toLowerCase()
      });
      navigateTo(marketValue.toLowerCase());
    };
    $[17] = navigateTo;
    $[18] = selected;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const handleRegionChange = t14;
  const t15 = !isMulti && "rounded-full border-2 border-gray-200 focus:border-sky-500";
  const t16 = isMulti && "rounded-l-full";
  const t17 = disabled && "cursor-not-allowed opacity-60";
  let t18;
  if ($[20] !== className || $[21] !== t15 || $[22] !== t16 || $[23] !== t17) {
    t18 = cn("relative cursor-pointer bg-none py-2 pr-12 pl-4 text-left transition-all duration-200 focus:ring-2 focus:ring-sky-500 focus:outline-none", t15, t16, t17, className);
    $[20] = className;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== disabled || $[26] !== regionOpen) {
    t19 = () => {
      if (!disabled) {
        setLangOpen(false);
        setRegionOpen(!regionOpen);
      }
    };
    $[25] = disabled;
    $[26] = regionOpen;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== selectedCountry) {
    t20 = selectedCountry && <FlagWrapper country={selectedCountry.value} />;
    $[28] = selectedCountry;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const t21 = lightBackground ? "text-black" : "text-white";
  let t22;
  if ($[30] !== t21) {
    t22 = cn("font-body font-medium", t21);
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== regionLabel || $[33] !== t22) {
    t23 = <span className={t22}>{regionLabel}</span>;
    $[32] = regionLabel;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] !== t20 || $[36] !== t23) {
    t24 = <span className="flex items-center gap-3">{t20}{t23}</span>;
    $[35] = t20;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  const t25 = regionOpen ? "text-sky-500" : "text-gray-200";
  let t26;
  if ($[38] !== t25) {
    t26 = cn("transition-colors", t25);
    $[38] = t25;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t26) {
    t27 = <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"><ArrowDownIcon className={t26} aria-hidden="true" /></span>;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  const regionButton = <button ref={regionBtnRef} type="button" className={t18} disabled={disabled} aria-haspopup="listbox" aria-expanded={regionOpen} aria-controls={`region-listbox-${uniqueId}`} aria-label={isMulti ? t("marketSelector.regionAria", {
    value: regionLabel
  }) : t("a11y.selectCountry")} onClick={t19}>{t24}{t27}</button>;
  const regionListbox = regionOpen && <div ref={regionListRef} id={`region-listbox-${uniqueId}`} role="listbox" tabIndex={-1} aria-label={t("a11y.selectCountry")} onKeyDown={e_1 => handleListboxKeyDown(e_1, regionListRef.current)} className="absolute bottom-full left-0 z-50 mb-1 max-h-60 w-max overflow-auto border border-gray-200 bg-white shadow-lg"><ul className="py-1">{MARKET_OPTIONS.map(market => {
        const isSelected = selectedCountry?.value === market.value;
        return <li key={`market-option-${market.value}-${uniqueId}`}><button type="button" role="option" aria-selected={isSelected} disabled={isSelected} className={cn("relative flex w-full cursor-pointer items-center gap-3 py-3 pr-12 pl-4 text-sm transition-colors duration-150 select-none hover:bg-gray-100", isSelected ? "cursor-not-allowed bg-sky-50 text-sky-900" : "text-gray-900")} onClick={() => {
            if (!isSelected) {
              handleRegionChange(market.value);
              setRegionOpen(false);
            }
          }}><FlagWrapper country={market.value} /><span className="font-body flex-1 text-start font-medium">{localizedCountryName(t, market.value, market.label)} ({market.currencySymbol})</span>{isSelected && <span className="absolute right-4 flex items-center text-sky-600"><CheckIcon className="h-5 w-5" aria-hidden="true" /></span>}</button></li>;
      })}</ul></div>;
  const languageHalf = isMulti && <><span aria-hidden="true" className="mx-1 w-px self-stretch bg-gray-300" /><div className="relative"><button ref={langBtnRef} type="button" className={cn("flex min-h-[44px] cursor-pointer items-center gap-2 rounded-r-full px-4 py-2 transition-colors hover:bg-gray-100/10 focus:ring-2 focus:ring-sky-500 focus:outline-none", disabled && "cursor-not-allowed opacity-60")} disabled={disabled} aria-haspopup="listbox" aria-expanded={langOpen} aria-controls={`lang-listbox-${uniqueId}`} aria-label={t("marketSelector.languageAria", {
        value: nativeLanguageName(currentLang)
      })} onClick={() => {
        if (!disabled) {
          setRegionOpen(false);
          setLangOpen(!langOpen);
        }
      }}><span className={cn("font-body font-medium", lightBackground ? "text-black" : "text-white")}>{nativeLanguageName(currentLang)}</span><ArrowDownIcon className={cn("transition-all", langOpen && "rotate-180", langOpen ? "text-sky-500" : "text-gray-200")} aria-hidden="true" /></button>{langOpen && <div ref={langListRef} id={`lang-listbox-${uniqueId}`} role="listbox" tabIndex={-1} aria-label={t("marketSelector.chooseLanguage")} onKeyDown={e_2 => handleListboxKeyDown(e_2, langListRef.current)} className="absolute bottom-full left-0 z-50 mb-1 max-h-60 w-max overflow-auto border border-gray-200 bg-white shadow-lg"><ul className="py-1">{langs.map(lang => {
            const isSel = lang === currentLang;
            return <li key={`lang-${lang}-${uniqueId}`}><button type="button" role="option" aria-selected={isSel} disabled={isSel} className={cn("relative flex w-full cursor-pointer items-center gap-3 py-3 pr-12 pl-4 text-sm select-none hover:bg-gray-100", isSel ? "cursor-not-allowed bg-sky-50 text-sky-900" : "text-gray-900")} onClick={() => {
                if (isSel) {
                  return;
                }
                setLangPref(baseMarket, lang);
                trackAction("language_selector_change", {
                  from: currentLang,
                  to: lang
                });
                navigateTo(composeMarketSlug(baseMarket, lang));
                setLangOpen(false);
              }}><span className="font-body flex-1 text-start font-medium">{nativeLanguageName(lang)}</span>{isSel && <span className="absolute right-4 flex items-center text-sky-600"><CheckIcon className="h-5 w-5" aria-hidden="true" /></span>}</button></li>;
          })}</ul></div>}</div></>;
  let t28;
  if ($[42] !== wrapperClassName) {
    t28 = cn("relative my-4 w-fit", wrapperClassName);
    $[42] = wrapperClassName;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  const t29 = isMulti ? <div role="group" aria-label={t("marketSelector.regionAndLanguage")} className="flex items-center rounded-full border-2 border-gray-200"><div className="relative">{regionButton}{regionListbox}</div>{languageHalf}</div> : <>{regionButton}{regionListbox}</>;
  let t30;
  if ($[44] !== isPending) {
    t30 = isPending && <div className="absolute inset-0 z-50 flex items-center justify-center rounded-full bg-white/80"><LoadingSpinner className="-mt-3 h-5 w-5" /></div>;
    $[44] = isPending;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== t28 || $[47] !== t29 || $[48] !== t30) {
    t31 = <div className={t28} ref={wrapperRef}>{t29}{t30}</div>;
    $[46] = t28;
    $[47] = t29;
    $[48] = t30;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  return t31;
};
function _temp(container) {
  return container ? Array.from(container.querySelectorAll("[role=\"option\"]:not([disabled])")) : [];
}